import './ProductItemRaffleDetails.css';
import { ProductItemModel } from '../../../models/product.model';
import { RaffleCountDownModel, UserProviderValue } from '../../../models';
import { useContext, useEffect, useMemo, useState } from 'react';
import { getWinningProbability } from '../../../utils/product.utils';
import { ProductItemDetailBox } from './ProductItemDetailBox';
import { formatNumber } from '../../../utils/numbers.utils';
import { UserContext } from '../../../contexts/user.context';
import { Button, Checkbox, LoadingSymbol, QuantityInput } from '../../ui';
import { Link } from 'react-router-dom';

export function ProductItemRaffleDetails(props: {
   productItem: ProductItemModel;
   raffleCountDown: RaffleCountDownModel | null;
   addToCartQuantity: number;
   setAddToCartQuantity: (quantity: number) => void;
   handleBuyEntries: () => void;
   userTickets: number;
   isLoading: boolean;
   tosAccepted: boolean;
   setTosAccepted: (tosAccepted: boolean) => void;
}) {
   const { user, isAuthenticated } = useContext<UserProviderValue>(UserContext);
   const { productItem, raffleCountDown, addToCartQuantity, setAddToCartQuantity, handleBuyEntries, userTickets, isLoading, tosAccepted, setTosAccepted } = props;
   const initialWinningProbability = useMemo(() => getWinningProbability(productItem), [productItem]);
   const [simulatedWinningProbability, setSimulatedWinningProbability] = useState(initialWinningProbability);

   useEffect(() => {
      if (!productItem.raffleStats) return;
      setSimulatedWinningProbability(getWinningProbability(productItem, addToCartQuantity));
   }, [productItem.raffleStats, addToCartQuantity]);

   return (
      <div className="flex flex-col w-100 justify-between h-100">
         <div className="flex justify-center">{!productItem?.raffleStats && <LoadingSymbol />}</div>
         <div className={`raffle-details flex flex-col justify-between gap-3 h-100 ${!productItem?.raffleStats ? 'hidden' : ''}`}>
            <div className="flex flex-col gap">
               <div className="flex gap wrap">
                  <ProductItemDetailBox
                     className="flex-1"
                     title="Drawn">
                     <span>
                        {productItem.raffleStats?.winners.length}/{productItem.stock}
                     </span>
                  </ProductItemDetailBox>
                  {raffleCountDown && (
                     <ProductItemDetailBox
                        className="flex-3"
                        title={raffleCountDown?.title.long ?? ''}>
                        <div className="raffle-stat-value flex gap-1">
                           {raffleCountDown.hours !== null && raffleCountDown.hours >= 24 && (
                              <span>
                                 {Math.floor(raffleCountDown.hours / 24)}
                                 <span className="">d</span>
                              </span>
                           )}
                           {raffleCountDown.hours !== null && (
                              <span>
                                 {raffleCountDown.hours % 24}
                                 <span className="">h</span>
                              </span>
                           )}
                           {raffleCountDown.minutes !== null && (raffleCountDown.hours === null || raffleCountDown.hours < 24) && (
                              <span>
                                 {raffleCountDown.minutes}
                                 <span className="">m</span>
                              </span>
                           )}

                           {raffleCountDown?.minutes === null && raffleCountDown?.hours === null && <span>ENDED</span>}
                        </div>
                     </ProductItemDetailBox>
                  )}
               </div>

               <div className="flex gap wrap">
                  <ProductItemDetailBox
                     className="flex-2"
                     title={`Entries ${isAuthenticated ? `(${formatNumber(productItem.userEntries)} by you)` : ''}`}>
                     <span>{formatNumber(productItem.totalEntries ?? 0)}</span>
                  </ProductItemDetailBox>
                  <ProductItemDetailBox
                     className="flex-1"
                     title="Probability">
                     {simulatedWinningProbability > initialWinningProbability ? (
                        <div className="flex items-center gap-1">
                           <div className="green">{formatNumber(simulatedWinningProbability)}%</div>
                        </div>
                     ) : (
                        <div>{formatNumber(initialWinningProbability)}%</div>
                     )}
                  </ProductItemDetailBox>
               </div>

               {(productItem.raffleStats?.winners?.length ?? 0) > 0 && (
                  <ProductItemDetailBox title={`DRAWN WINNER${(productItem.raffleStats?.winners?.length || 2) > 1 || (productItem.stock ?? 0) > 1 ? 'S' : ''}`}>
                     {productItem.raffleStats?.winners?.length === 0 ? (
                        'soon...'
                     ) : (
                        <ul className="flex flex-col gap">
                           {productItem.raffleStats?.winners.map((winner, index) => (
                              <li
                                 key={index}
                                 className={`winner-user ${user?.id === winner.id ? 'you' : ''}`}>
                                 <div
                                    className="avatar"
                                    style={{ backgroundImage: `url(${winner.image ?? '/assets/icons/user-icon.png'})` }}></div>
                                 <span>{winner.friendlyDisplay ?? winner.username ?? winner.publicKey?.substring(0, 5)}</span>
                              </li>
                           ))}
                        </ul>
                     )}
                  </ProductItemDetailBox>
               )}
            </div>
            {raffleCountDown?.isLive && (
               <div className={`product-item-actions-container`}>
                  <div className={`flex flex-col gap-3`}>
                     <div className="flex flex-col gap-small">
                        <div className="flex justify-center">
                           <span className="gray small">1 ticket = 1 entry</span>
                        </div>
                        <QuantityInput
                           quantity={addToCartQuantity}
                           setQuantity={setAddToCartQuantity}
                           disabled={!raffleCountDown?.isLive}
                        />
                     </div>

                     <div className="flex flex-col gap-2">
                        {productItem.product.tosLink && (
                           <Checkbox
                              checked={tosAccepted ?? productItem.userEntries > 0}
                              onChange={() => setTosAccepted(!tosAccepted)}>
                              <span className="flex items-center gap-1 medium">
                                 I have read and accepted the
                                 <Link
                                    to={productItem.product.tosLink ?? ''}
                                    target="_blank"
                                    className="underline">
                                    Terms and Conditions.
                                 </Link>
                              </span>
                           </Checkbox>
                        )}

                        <Button
                           title={`${!isAuthenticated ? 'Login to Enter' : 'Confirm Entries'}`}
                           type="white-big"
                           disabled={!raffleCountDown?.isLive || addToCartQuantity === 0 || userTickets < addToCartQuantity || isLoading || !tosAccepted}
                           onClick={handleBuyEntries}
                           loading={isLoading}
                        />
                     </div>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
}
